/**
 * IntelliGems A/B Test: PDP reviews social proof (Aerless-style)
 *
 * Control (A): data-pdp-reviews-ab="false"
 * Variant (B): data-pdp-reviews-ab="true"
 *
 * Dev preview: ?pdp_reviews_ab_test=b
 *
 * Reads the existing Ryviu rating badge above the product title
 * and renders: avatars + "[reviewer] and 10k+ happy customers" + dynamic stars/count.
 * Reviewer name is taken from the first Ryviu review on the PDP.
 */
(function () {
  'use strict';

  if (window.__pdpReviewsABTestInit) return;
  window.__pdpReviewsABTestInit = true;

  var BODY_ATTR = 'data-pdp-reviews-ab';
  var SOURCE_ATTR = 'data-pdp-reviews-ab-source';
  var MARK = 'data-pdp-reviews-ab-hydrated';
  var DEFAULT_RATING = '4.9';
  var REVIEWS_ANCHOR_ID = 'ryviu-widget-reviews';
  var STAR_PATH = 'M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.77l-4.94 2.94.94-5.5-4-3.9 5.53-.8L10 1.5z';

  var isApplying = false;
  var debounceTimer = null;
  var retryTimer = null;
  var retryCount = 0;
  var sourceObserver = null;
  var reviewsObserver = null;
  var nameRetryTimer = null;
  var nameRetryCount = 0;
  var fetchedName = '';
  var nameFetchStarted = false;

  function isDevVariantB() {
    try {
      return new URLSearchParams(window.location.search).get('pdp_reviews_ab_test') === 'b';
    } catch (e) {
      return false;
    }
  }

  function isTruthy(value) {
    var normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'on' || normalized === 'yes';
  }

  function isVariantB() {
    if (isDevVariantB()) return true;

    var body = document.body;
    var html = document.documentElement;
    if (!body || !html) return false;

    return isTruthy(body.getAttribute(BODY_ATTR)) || isTruthy(html.getAttribute(BODY_ATTR));
  }

  function isProductPage() {
    if (!document.body) return false;
    if (document.body.classList.contains('template-product')) return true;
    if (window.theme && window.theme.pageType === 'product') return true;
    return /\/products\//.test(window.location.pathname);
  }

  function formatCount(value) {
    var digits = String(value || '').replace(/[^\d]/g, '');
    if (!digits) return '';
    return Number(digits).toLocaleString('en-US');
  }

  function parseReviewsInfo(raw) {
    if (!raw) return null;

    var text = String(raw).trim();
    if (!text) return null;

    try {
      var parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object') {
        var rating =
          parsed.avg_rate ||
          parsed.avg ||
          parsed.r_rating ||
          parsed.rating ||
          parsed.average_score ||
          parsed.score;
        var count =
          parsed.total_review ||
          parsed.total ||
          parsed.r_count ||
          parsed.count ||
          parsed.review_count ||
          parsed.total_reviews;

        if (rating || count) {
          return {
            rating: rating ? String(rating) : DEFAULT_RATING,
            count: formatCount(count)
          };
        }
      }
    } catch (e) {
      /* not JSON */
    }

    var pairMatch = text.match(/(\d+(?:\.\d+)?)\s*[|;:,\/]\s*([\d,]+)/) ||
      text.match(/([\d,]+)\s*[|;:,\/]\s*(\d+(?:\.\d+)?)/);

    if (pairMatch) {
      var first = pairMatch[1];
      var second = pairMatch[2];
      if (first.indexOf('.') !== -1) {
        return { rating: first, count: formatCount(second) };
      }
      return { rating: second, count: formatCount(first) };
    }

    return null;
  }

  function parseRatingText(labelText) {
    var text = String(labelText || '').replace(/\s+/g, ' ').trim();
    if (!text) return null;

    var ratingMatch = text.match(/(\d+(?:\.\d+)?)\s*\/\s*5/) ||
      text.match(/\b([1-5]\.\d+)\b/) ||
      text.match(/\b([1-5])\s+(?:\(|reviews?)/i);
    var countMatch = text.match(/\(([\d,]+)\s*(?:customer\s+)?reviews?\)/i) ||
      text.match(/([\d,]+)\s+(?:customer\s+)?reviews?/i);

    if (!ratingMatch && !countMatch) return null;

    return {
      rating: ratingMatch ? ratingMatch[1] : DEFAULT_RATING,
      count: countMatch ? formatCount(countMatch[1]) : ''
    };
  }

  function getTitleEl() {
    return document.querySelector('gp-product-title') ||
      document.querySelector('.ProductMeta__Title') ||
      document.querySelector('.ProductMeta h1') ||
      document.querySelector('[data-gp-text] h2');
  }

  function closestInfoScope(node) {
    if (!node) return document.body;
    return node.closest('.Product__Info, .Product__InfoWrapper, .ProductMeta, [class*="gp-flex-col"]') ||
      node.parentElement ||
      document.body;
  }

  function isCollectionWidget(node) {
    if (!node) return true;
    if (node.hasAttribute && node.hasAttribute('collection')) return true;
    if (node.closest && node.closest('.ryviu-collection, .ProductItem, .product-item')) return true;
    return false;
  }

  function getRatingSources() {
    var title = getTitleEl();
    var scope = closestInfoScope(title);
    var sources = [];

    function addSource(node) {
      if (!node || sources.indexOf(node) !== -1) return;
      if (isCollectionWidget(node)) return;
      sources.push(node);
    }

    if (scope) {
      Array.prototype.forEach.call(scope.querySelectorAll('.review-widget, ryviu-widget-total, .r--widget-total'), addSource);
    }

    if (!sources.length) {
      Array.prototype.forEach.call(
        document.querySelectorAll('.Product__Info .review-widget, .ProductMeta .review-widget, ryviu-widget-total:not([collection])'),
        addSource
      );
    }

    return sources;
  }

  function getPrimaryRatingSource() {
    var sources = getRatingSources();
    return sources.length ? sources[0] : null;
  }

  function extractFromWidget(source) {
    if (!source) return null;

    var widget = source.tagName && source.tagName.toLowerCase() === 'ryviu-widget-total'
      ? source
      : source.querySelector('ryviu-widget-total');

    if (widget) {
      var fromAttr = parseReviewsInfo(widget.getAttribute('reviews_data') || widget.getAttribute('total_meta'));
      if (fromAttr && fromAttr.count) return fromAttr;
    }

    var ratingEl = source.querySelector('[class*="rating"], [class*="score"], .r--rating-total, .r-rating');
    var countEl = source.querySelector('[class*="review"], [class*="total"], .r--total-reviews');
    var combined = [
      ratingEl && ratingEl.textContent,
      countEl && countEl.textContent,
      source.textContent
    ].filter(Boolean).join(' ');

    return parseRatingText(combined);
  }

  function extractRatingData(root) {
    var fromWidget = extractFromWidget(getPrimaryRatingSource());
    if (fromWidget && fromWidget.count) return fromWidget;

    if (root) {
      var fromMeta = parseReviewsInfo(root.getAttribute('data-reviews-info'));
      if (fromMeta && fromMeta.count) return fromMeta;

      var fallbackCount = formatCount(root.getAttribute('data-review-count'));
      if (fallbackCount) {
        return { rating: DEFAULT_RATING, count: fallbackCount };
      }
    }

    return fromWidget;
  }

  function starMarkup(fill) {
    var pct = Math.round(Math.min(1, Math.max(0, fill)) * 100);
    return (
      '<span class="pdp-reviews-ab__star">' +
        '<svg class="pdp-reviews-ab__star-bg" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="' + STAR_PATH + '"/></svg>' +
        '<span class="pdp-reviews-ab__star-fill" style="width:' + pct + '%">' +
          '<svg viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="' + STAR_PATH + '"/></svg>' +
        '</span>' +
      '</span>'
    );
  }

  function renderStars(rating) {
    var html = '';
    for (var i = 1; i <= 5; i += 1) {
      html += starMarkup(i < 5 ? 1 : 0.5);
    }
    return html;
  }

  function buildSnippetHtml() {
    var template = document.getElementById('pdp-reviews-ab-template');
    if (template && template.content && template.content.firstElementChild) {
      return template.content.firstElementChild.cloneNode(true);
    }

    var wrap = document.createElement('div');
    wrap.innerHTML =
      '<div class="pdp-reviews-ab" data-pdp-reviews-ab-root aria-label="Customer reviews and ratings">' +
        '<div class="pdp-reviews-ab__social-row">' +
          '<div class="pdp-reviews-ab__avatars" aria-hidden="true">' +
            '<img class="pdp-reviews-ab__avatar" src="https://randomuser.me/api/portraits/women/44.jpg" alt="" width="30" height="30" loading="lazy">' +
            '<img class="pdp-reviews-ab__avatar" src="https://randomuser.me/api/portraits/women/68.jpg" alt="" width="30" height="30" loading="lazy">' +
            '<img class="pdp-reviews-ab__avatar" src="https://randomuser.me/api/portraits/women/21.jpg" alt="" width="30" height="30" loading="lazy">' +
          '</div>' +
          '<p class="pdp-reviews-ab__purchase-text">' +
            '<strong class="pdp-reviews-ab__name" data-pdp-reviews-ab-name>Kim</strong>' +
            '<span class="pdp-reviews-ab__verified" aria-label="Verified customer">' +
              '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
                '<circle cx="7" cy="7" r="7" fill="#1877F2"/>' +
                '<path d="M4 7.2L6.1 9.3L10 5.3" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
            '</span>' +
            ' and 10k+ happy customers' +
          '</p>' +
        '</div>' +
        '<a class="pdp-reviews-ab__reviews-link" data-pdp-reviews-ab-reviews-link href="#ryviu-widget-reviews" aria-label="Read customer reviews">' +
          '<div class="pdp-reviews-ab__rating-row">' +
            '<div class="pdp-reviews-ab__stars" data-pdp-reviews-ab-stars aria-hidden="true"></div>' +
            '<span class="pdp-reviews-ab__rating-text" data-pdp-reviews-ab-rating-text>4.9 / 5</span>' +
          '</div>' +
        '</a>' +
      '</div>';
    return wrap.firstElementChild;
  }

  function getInsertTarget() {
    var source = getPrimaryRatingSource();
    if (source) {
      var host = source.closest('.review-widget') || source;
      return { type: 'source', node: host };
    }

    var title = getTitleEl();
    if (title) return { type: 'title', node: title };
    return null;
  }

  function ensureSnippet() {
    var existing = document.querySelector('[data-pdp-reviews-ab-root]');
    if (existing) return existing;

    var target = getInsertTarget();
    if (!target) return null;

    var snippet = buildSnippetHtml();
    if (!snippet) return null;

    if (target.type === 'source') {
      target.node.parentNode.insertBefore(snippet, target.node);
    } else {
      target.node.parentNode.insertBefore(snippet, target.node);
    }

    return snippet;
  }

  function setSourcesHidden(hidden) {
    getRatingSources().forEach(function (source) {
      var host = source.closest('.review-widget') || source;
      if (hidden) {
        host.setAttribute(SOURCE_ATTR, 'true');
      } else {
        host.removeAttribute(SOURCE_ATTR);
      }
    });
  }

  function getReviewsWidgetEl() {
    return document.getElementById(REVIEWS_ANCHOR_ID) ||
      document.querySelector('.r--widget-section') ||
      document.querySelector('[id*="ryviu_reviews_qa_ryviu_widget"]') ||
      document.querySelector('ryviu-widget:not([collection]):not([feature])') ||
      document.getElementById('r-customer-review') ||
      document.querySelector(
        '.Ryviu-Header, .header-v1, ryviu-widget, .ryviu-widget, #ryviu-widget, #r--ryviu-widget, .lt-block-reviews'
      );
  }

  function ensureReviewsAnchor(el) {
    var existing = document.getElementById(REVIEWS_ANCHOR_ID);
    if (existing) return '#' + REVIEWS_ANCHOR_ID;
    if (!el) return '#' + REVIEWS_ANCHOR_ID;

    if (el.id === REVIEWS_ANCHOR_ID) return '#' + REVIEWS_ANCHOR_ID;

    var mark = document.createElement('div');
    mark.id = REVIEWS_ANCHOR_ID;
    mark.setAttribute('data-pdp-reviews-ab-anchor', 'true');
    mark.setAttribute('aria-hidden', 'true');
    mark.style.cssText = 'position:relative;width:0;height:0;overflow:hidden;';
    if (el.firstChild) {
      el.insertBefore(mark, el.firstChild);
    } else {
      el.appendChild(mark);
    }
    return '#' + REVIEWS_ANCHOR_ID;
  }

  function getReviewsTarget() {
    return ensureReviewsAnchor(getReviewsWidgetEl());
  }

  var scrollRetry = 0;

  function scrollToReviews() {
    var el = getReviewsWidgetEl();
    if (!el) {
      if (scrollRetry < 16) {
        scrollRetry += 1;
        window.setTimeout(scrollToReviews, 250);
      }
      return;
    }

    scrollRetry = 0;
    ensureReviewsAnchor(el);

    var header = document.querySelector('#shopify-section-header, sticky-header, .Header, header');
    var offset = 16;
    if (header) {
      var position = window.getComputedStyle(header).position;
      if (position === 'fixed' || position === 'sticky') {
        offset += header.getBoundingClientRect().height;
      }
    }

    var top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function getShopDomain() {
    if (window.Shopify && Shopify.shop) return Shopify.shop;
    var root = document.querySelector('[data-pdp-reviews-ab-root]');
    return (root && root.getAttribute('data-shop-domain')) || window.location.hostname;
  }

  function reviewsFromPayload(data) {
    if (!data) return [];
    if (Array.isArray(data.more_reviews)) return data.more_reviews;
    if (Array.isArray(data.reviews)) return data.reviews;
    if (data.reviews && Array.isArray(data.reviews.data)) return data.reviews.data;
    if (data.reviews && Array.isArray(data.reviews.reviews)) return data.reviews.reviews;
    if (Array.isArray(data.data)) return data.data;
    return [];
  }

  function nameFromRyviuPayload(data) {
    var reviews = reviewsFromPayload(data);
    var first = reviews[0] || {};
    return normalizeReviewerName(
      first.author || first.name || first.customer_name || first.display_name || first.author_name
    );
  }

  function applyFetchedName(name) {
    if (!name) return;
    fetchedName = name;
    var root = document.querySelector('[data-pdp-reviews-ab-root]');
    if (root) hydrateReviewerName(root);
  }

  function fetchReviewerNameFromApi(root) {
    if (nameFetchStarted) return;
    nameFetchStarted = true;

    var handle = (root && root.getAttribute('data-product-handle')) || '';
    var productId = (root && root.getAttribute('data-product-id')) || '';
    var widget = document.querySelector('.r--widget-section ryviu-widget, ryviu-widget:not([collection]):not([feature])');
    if (widget) {
      handle = handle || widget.getAttribute('handle') || '';
      productId = productId || widget.getAttribute('product_id') || '';
    }
    if (!handle && !productId) return;

    var domain = getShopDomain();
    var body = JSON.stringify({
      handle: handle,
      domain: domain,
      product_id: productId ? Number(productId) || productId : 0,
      platform: 'shopify',
      page: 1,
      type: 'load-more',
      order: 'late',
      filter: 'all',
      first_load: true,
      locale: document.documentElement.lang || 'en'
    });

    fetch('https://app.ryviu.io/frontend/client/get-more-reviews?domain=' + encodeURIComponent(domain), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        applyFetchedName(nameFromRyviuPayload(data));
      })
      .catch(function () {});
  }

  function watchRyviuNetwork() {
    if (window.__pdpReviewsABFetchHook || typeof window.fetch !== 'function') return;
    window.__pdpReviewsABFetchHook = true;
    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      return originalFetch.apply(this, args).then(function (res) {
        try {
          var url = String((args[0] && args[0].url) || args[0] || '');
          if (url.indexOf('ryviu.io') !== -1 && url.indexOf('frontend/client') !== -1) {
            res.clone().json().then(function (data) {
              applyFetchedName(nameFromRyviuPayload(data));
            }).catch(function () {});
          }
        } catch (e) { /* ignore */ }
        return res;
      });
    };
  }

  function normalizeReviewerName(raw) {
    var text = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';

    text = text.replace(/^[-–—•]+\s*/, '').replace(/\s*[-–—•]+$/, '');
    var first = text.split(/[\s,|\/]+/)[0] || '';
    first = first.replace(/[^A-Za-zÀ-ž'.-]/g, '');
    if (first.length < 2 || first.length > 24) return '';

    var blocked = /^(anonymous|customer|verified|buyer|reviewer|admin|user|guest|unknown)$/i;
    if (blocked.test(first)) return '';

    return first.charAt(0).toUpperCase() + first.slice(1);
  }

  function extractReviewerName() {
    var nodes = document.querySelectorAll('.Ryviu-Author-Name, .Ryviu-Name-Date strong');
    for (var i = 0; i < nodes.length; i += 1) {
      if (nodes[i].closest && nodes[i].closest('[data-pdp-reviews-ab-root]')) continue;
      var name = normalizeReviewerName(nodes[i].textContent);
      if (name) return name;
    }

    var widget = getReviewsWidgetEl();
    var scope = widget
      ? (widget.closest('.Ryviu-Header, .shopify-app-block, section, .Product, body') || widget)
      : document;
    var selectors = [
      '.r-author',
      '.r--author',
      '.rv-author',
      '.author-name',
      '.r-name',
      '.r-user-name',
      '.user-name',
      '.ryviu-author',
      '.author .name',
      '[itemprop="author"] [itemprop="name"]',
      '[itemprop="author"]',
      '.r-review-item .name',
      '.review-author'
    ];

    for (var j = 0; j < selectors.length; j += 1) {
      var extras = scope.querySelectorAll(selectors[j]);
      for (var k = 0; k < extras.length; k += 1) {
        if (extras[k].closest && extras[k].closest('[data-pdp-reviews-ab-root]')) continue;
        var extraName = normalizeReviewerName(extras[k].textContent);
        if (extraName) return extraName;
      }
    }

    return '';
  }

  function hydrateReviewerName(root) {
    if (!root) return false;

    var nameEl = root.querySelector('[data-pdp-reviews-ab-name], .pdp-reviews-ab__name');
    if (!nameEl) return false;

    var name = fetchedName || extractReviewerName();
    if (!name) {
      scheduleNameRetry();
      return false;
    }

    stopNameRetry();

    if (nameEl.textContent !== name) {
      nameEl.textContent = name;
    }
    nameEl.setAttribute('data-name', name);
    root.setAttribute('data-customer-name', name);
    return true;
  }

  function scheduleNameRetry() {
    if (!isVariantB() || nameRetryCount >= 40 || nameRetryTimer) return;
    nameRetryTimer = window.setTimeout(function () {
      nameRetryTimer = null;
      nameRetryCount += 1;
      var root = document.querySelector('[data-pdp-reviews-ab-root]');
      if (root) hydrateReviewerName(root);
    }, 400);
  }

  function stopNameRetry() {
    nameRetryCount = 0;
    if (nameRetryTimer) {
      window.clearTimeout(nameRetryTimer);
      nameRetryTimer = null;
    }
  }

  function hydrateRoot(root) {
    if (!root) return;

    var parsed = extractRatingData(root);
    var ratingTextEl = root.querySelector('[data-pdp-reviews-ab-rating-text]');
    var starsEl = root.querySelector('[data-pdp-reviews-ab-stars]');
    var link = root.querySelector('[data-pdp-reviews-ab-reviews-link]');
    var count = (parsed && parsed.count) || formatCount(root.getAttribute('data-review-count'));
    var rating = DEFAULT_RATING;
    var display = rating + ' / 5';

    hydrateReviewerName(root);

    if (!count) {
      scheduleRetry();
    } else {
      stopRetry();
      display += ' (' + count + ' reviews)';
    }

    if (ratingTextEl && ratingTextEl.textContent !== display) {
      ratingTextEl.textContent = display;
      ratingTextEl.setAttribute('data-rating', rating);
      if (count) ratingTextEl.setAttribute('data-review-count', count);
    }

    if (starsEl && starsEl.getAttribute('data-rating') !== String(rating)) {
      starsEl.innerHTML = renderStars(rating);
      starsEl.setAttribute('data-rating', String(rating));
    }

    if (link) {
      var target = getReviewsTarget();
      if (link.getAttribute('href') !== target) {
        link.setAttribute('href', target);
      }
    }

    root.setAttribute(MARK, 'true');
  }

  function applyVariant() {
    var variantB = isVariantB();

    document.documentElement.classList.toggle('pdp-reviews-ab-b', variantB);

    if (!isProductPage()) {
      setSourcesHidden(false);
      return;
    }

    if (!variantB) {
      setSourcesHidden(false);
      return;
    }

    var root = ensureSnippet();
    setSourcesHidden(true);
    ensureReviewsAnchor(getReviewsWidgetEl());
    watchRyviuNetwork();
    fetchReviewerNameFromApi(root);
    hydrateRoot(root);
    watchSource();
    watchReviews();
  }

  function scheduleApply() {
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(function () {
      debounceTimer = null;
      if (isApplying) return;
      isApplying = true;
      try {
        applyVariant();
      } finally {
        isApplying = false;
      }
    }, 120);
  }

  function scheduleRetry() {
    if (!isVariantB() || retryCount >= 10 || retryTimer) return;
    retryTimer = window.setTimeout(function () {
      retryTimer = null;
      retryCount += 1;
      scheduleApply();
    }, 350);
  }

  function stopRetry() {
    retryCount = 0;
    if (retryTimer) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
  }

  function watchSource() {
    if (sourceObserver) return;

    var source = getPrimaryRatingSource();
    var scope = source ? closestInfoScope(source) : document.body;
    if (!scope) return;

    sourceObserver = new MutationObserver(function () {
      if (isVariantB()) scheduleApply();
    });

    sourceObserver.observe(scope, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['reviews_data', 'total_meta', 'data-rating']
    });
  }

  function watchReviews() {
    if (reviewsObserver) return;

    var widget = document.body;
    if (!widget) return;

    reviewsObserver = new MutationObserver(function () {
      if (!isVariantB()) return;
      var root = document.querySelector('[data-pdp-reviews-ab-root]');
      if (root) hydrateReviewerName(root);
    });

    reviewsObserver.observe(widget, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function init() {
    if (isDevVariantB()) {
      if (document.documentElement.getAttribute(BODY_ATTR) !== 'true') {
        document.documentElement.setAttribute(BODY_ATTR, 'true');
      }
      if (document.body && document.body.getAttribute(BODY_ATTR) !== 'true') {
        document.body.setAttribute(BODY_ATTR, 'true');
      }
    }

    watchRyviuNetwork();
    scheduleApply();

    if (!document.body) return;

    document.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('[data-pdp-reviews-ab-reviews-link]');
      if (!link) return;
      event.preventDefault();
      scrollToReviews();
    });

    new MutationObserver(scheduleApply).observe(document.body, {
      attributes: true,
      attributeFilter: [BODY_ATTR, 'data-gem-variant', 'data-gem-test-active']
    });

    document.addEventListener('gem:variant:changed', scheduleApply);
    document.addEventListener('shopify:section:load', scheduleApply);
  }

  window.PdpReviewsABTest = {
    enable: function () {
      if (!document.body) return;
      document.body.setAttribute(BODY_ATTR, 'true');
      scheduleApply();
    },
    disable: function () {
      if (!document.body) return;
      document.body.setAttribute(BODY_ATTR, 'false');
      scheduleApply();
    },
    isActive: isVariantB,
    hydrate: scheduleApply
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
